/** Modal to import markdown via paste/type or load from a local .md file. */

export type ImportMarkdownDraft = {
  fileName: string;
  content: string;
};

let modalEl: HTMLElement | null = null;
let resolveOpen: ((value: ImportMarkdownDraft | null) => void) | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;

function closeModal(value: ImportMarkdownDraft | null): void {
  if (!resolveOpen) return;
  const r = resolveOpen;
  resolveOpen = null;
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }
  modalEl?.classList.add('hidden');
  document.body.style.overflow = '';
  r(value);
}

function trySubmitFromModal(): void {
  if (!modalEl) return;
  const nameInput = modalEl.querySelector<HTMLInputElement>('.import-md-modal-name')!;
  const contentInput = modalEl.querySelector<HTMLTextAreaElement>('.import-md-modal-content')!;
  const fileName = nameInput.value.trim();
  const content = contentInput.value.replace(/\r\n/g, '\n');
  if (!fileName) {
    nameInput.focus();
    return;
  }
  if (!content.trim()) {
    contentInput.focus();
    return;
  }
  closeModal({ fileName, content });
}

function ensureModal(): HTMLElement {
  if (modalEl) return modalEl;

  const modal = document.createElement('div');
  modal.id = 'import-md-modal';
  modal.className = 'import-md-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'import-md-modal-title');
  modal.innerHTML = `
    <div class="import-md-modal-backdrop" aria-hidden="true"></div>
    <div class="import-md-modal-card">
      <header class="import-md-modal-header">
        <h2 id="import-md-modal-title" class="import-md-modal-title">Import Markdown</h2>
        <button type="button" class="import-md-modal-close" aria-label="Close">×</button>
      </header>
      <p class="import-md-modal-lead">Paste markdown or load a file, then save it under <code>database/</code>. Use <code>## Section</code> headings or a title line, blank line, then <code>- items</code> for multiple sections.</p>
      <label class="import-md-modal-field">
        <span class="import-md-modal-label">File name</span>
        <input type="text" class="import-md-modal-name" placeholder="e.g. garden-notes.md" autocomplete="off" />
      </label>
      <div class="import-md-modal-toolbar">
        <button type="button" class="btn-ghost-dark import-md-pick-file">Choose .md file…</button>
      </div>
      <label class="import-md-modal-field import-md-modal-field-grow">
        <span class="import-md-modal-label">Content</span>
        <textarea class="import-md-modal-content" rows="14" placeholder="Paste or type markdown…"></textarea>
      </label>
      <input type="file" class="import-md-file-input hidden" accept=".md,text/markdown,text/plain" tabindex="-1" aria-hidden="true" />
      <footer class="import-md-modal-footer">
        <button type="button" class="btn-ghost-dark import-md-cancel">Cancel</button>
        <button type="button" class="btn-primary import-md-submit">Import</button>
      </footer>
    </div>
  `;

  const nameInput = modal.querySelector<HTMLInputElement>('.import-md-modal-name')!;
  const contentInput = modal.querySelector<HTMLTextAreaElement>('.import-md-modal-content')!;
  const fileInput = modal.querySelector<HTMLInputElement>('.import-md-file-input')!;

  modal.querySelector('.import-md-pick-file')!.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) return;
    void file.text().then(text => {
      contentInput.value = text.replace(/\r\n/g, '\n');
      if (!nameInput.value.trim()) nameInput.value = file.name;
      contentInput.focus();
    });
  });
  modal.querySelector('.import-md-submit')!.addEventListener('click', trySubmitFromModal);
  modal.querySelector('.import-md-cancel')!.addEventListener('click', () => closeModal(null));
  modal.querySelector('.import-md-modal-close')!.addEventListener('click', () => closeModal(null));
  modal.querySelector('.import-md-modal-backdrop')!.addEventListener('click', () => closeModal(null));

  document.body.appendChild(modal);
  modalEl = modal;
  return modal;
}

export function mountImportMarkdownModal(): void {
  ensureModal();
}

export function openImportMarkdownModal(): Promise<ImportMarkdownDraft | null> {
  mountImportMarkdownModal();
  const modal = ensureModal();
  const nameInput = modal.querySelector<HTMLInputElement>('.import-md-modal-name')!;
  const contentInput = modal.querySelector<HTMLTextAreaElement>('.import-md-modal-content')!;
  modal.querySelector<HTMLInputElement>('.import-md-file-input')!.value = '';

  nameInput.value = '';
  contentInput.value = '';

  return new Promise(resolve => {
    resolveOpen = resolve;
    keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeModal(null);
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        trySubmitFromModal();
      }
    };
    document.addEventListener('keydown', keyHandler);
    modal.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    nameInput.focus();
  });
}
